import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { EventBusService } from 'app/document/services/event-bus.service';
import * as GuidedTourActions from 'app/guided-tour/state/guided-tour.actions';
import { GuidedTourSelectors } from 'app/guided-tour/state/guided-tour.selectors';
import { GuidedTourDataService } from 'app/services/guided-tour-data.service';
import { Workout, WorkoutType } from 'fitforce-document-sync';
import { Observable, Subscription } from 'rxjs';
import { Day } from '../document-calendar/document-calendar.component';
import {
  CopyDayState,
  copyDayTopic,
  dayViewTopic,
} from '../document-planning.component';

@Component({
  selector: 'document-plan-day-actions',
  templateUrl: './document-plan-day-actions.component.html',
  styleUrls: ['./document-plan-day-actions.component.scss'],
})
export class DocumentPlanDayActionsComponent
  implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  copying = false;
  copyingSubscription: Subscription = new Subscription();
  dayView = false;
  dayViewSubscription: Subscription = new Subscription();
  guidedTourToggledOn = false;
  guidedTourStatusSub: Subscription;

  @Select(GuidedTourSelectors.toggleStatus)
  guidedTourToggleStatus$: Observable<boolean>;

  @Input() readonly day: Day;
  @Input() readonly isOnline: boolean;
  @Input() readonly isSynced: boolean;
  @Input() readonly canEdit: boolean;
  @Input() readonly isEditing: boolean;

  @Output() completed: EventEmitter<Workout> = new EventEmitter<Workout>();
  @Output() unfinished: EventEmitter<Workout> = new EventEmitter<Workout>();
  @Output() cleared: EventEmitter<void> = new EventEmitter<void>();
  @Output() addedToLibrary: EventEmitter<Workout> = new EventEmitter<Workout>();
  @Output() downloaded: EventEmitter<void> = new EventEmitter<void>();
  @Output() workoutSelected: EventEmitter<Day> = new EventEmitter<Day>();
  @Output() standardWorkoutAdded: EventEmitter<{
    day: Day;
    workoutType: WorkoutType;
  }> = new EventEmitter<{
    day: Day;
    workoutType: WorkoutType;
  }>();
  @Output() customWorkoutAdded: EventEmitter<{ day: Day }> = new EventEmitter<{
    day: Day;
  }>();
  @Output() restrictedWorkoutAdded: EventEmitter<{ day: Day }> =
    new EventEmitter<{ day: Day }>();
  @Output() workoutAddedFromLibrary: EventEmitter<{ day: Day }> =
    new EventEmitter<{ day: Day }>();
  @Output() programmingChartOpened: EventEmitter<void> =
    new EventEmitter<void>();

  get workout(): Workout {
    return this.day ? this.day.workout : undefined;
  }

  get workouts(): Workout[] {
    return this.day ? this.day.workouts : undefined;
  }

  get phase(): string {
    return this.day
      ? this.day.phase
        ? this.day.phase.name
        : undefined
      : undefined;
  }

  get date(): string {
    return this.day
      ? this.day.date.format('DMMMYYYY').toUpperCase()
      : undefined;
  }

  get title() {
    let title = '';
    if (this.workout) {
      title = `${this.workout.name} - ${this.date}`;
    }

    if (this.phase) {
      title = `${this.phase} - ${title}`;
    }

    return title;
  }

  get isRestricted() {
    return this.workout && this.workout.restrict_label_id;
  }

  constructor(
    private bus: EventBusService,
    private guidedTourDataService: GuidedTourDataService,
    private store: Store,
  ) {}

  ngOnInit() {
    this.copyingSubscription = this.bus.subscribe<CopyDayState>(
      copyDayTopic,
      result => {
        this.copying = result.copying;
      },
    );

    this.dayViewSubscription = this.bus.subscribe<boolean>(
      dayViewTopic,
      result => {
        if (result.hasOwnProperty('override')) {
          this.dayView = result['override'];
        } else {
          if (result && this.workout.complete && this.canEdit) {
            // if we want to edit and workout is currently marked as complete
            this.unfinished.emit(this.workout);
          }

          if (
            this.dayView && this.workout.est_completion_time_ms > -1 && this.workout.est_rpe > -1
          ) {
            this.dayView = result;
          } else {
            this.bus.publish<boolean>('dayInvalid', true);
          }
        }
      },
    );
  }

  ngAfterViewInit() {
    this.store.dispatch(new GuidedTourActions.GetGuidedTourToggleStatus());
    this.guidedTourStatusSub = this.guidedTourToggleStatus$.subscribe(
      status => {
        if (status !== undefined) {
          this.guidedTourToggledOn = status;
          if (this.guidedTourToggledOn) {
            setTimeout(
              () =>
                this.guidedTourDataService.startGuidedTour('workout-overview'),
              1000,
            );
          }
        }
      },
    );
  }

  ngOnChanges() {}

  ngOnDestroy() {
    this.copyingSubscription.unsubscribe();
    this.dayViewSubscription.unsubscribe();
  }

  switchView() {
    this.bus.publish(dayViewTopic, !this.dayView);
  }

  /**
   * Marks day as complete
   * If editing changes view: edit -> review
   */
  markDayCompleted() {
    if (
      !this.workout.est_completion_time_ms ||
      !this.workout.est_rpe ||
      this.workout.est_completion_time_ms === -1 ||
      this.workout.est_rpe === -1
    ) {
      this.bus.publish('dayInvalid', false);
      // this.unfinished.emit(this.workout);
      this.switchView();
    } else {
      this.completed.emit(this.workout);
      if (this.dayView) {
        this.bus.publish(dayViewTopic, false);
      }
    }
  }

  /**
   * Emit event: workout added to library
   */
  addWorkoutToLibrary() {
    this.addedToLibrary.emit(this.workout);
  }

  copyDay() {
    this.bus.publish<CopyDayState>(copyDayTopic, <CopyDayState>{
      cancelled: false,
      copying: true,
      pasted: false,
      workoutId: this.workout.id,
    });
  }

  cancelCopy() {
    this.bus.publish<CopyDayState>(copyDayTopic, <CopyDayState>{
      cancelled: true,
      copying: false,
      pasted: false,
      workoutId: this.workout.id,
    });
  }

  pasteDay() {
    this.bus.publish<CopyDayState>(copyDayTopic, <CopyDayState>{
      cancelled: false,
      copying: false,
      pasted: true,
      workoutId: this.workout.id,
    });
  }
}
